import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import {
  DeliveryEnvelope,
  DeliveryStatus,
  DeliveryPage,
  InstallationDescriptor,
  type TDeliveryEnvelope,
  type TDeliveryStatus,
} from '@schemas'
import { Button } from '@/components/ui/button'
import {
  transferRequest,
  useTransferAction,
  downloadJson,
  isUncertainTransferFailure,
} from '@/api/show-transfer'
import { Check, JsonFile, privacyWarning } from './files'
import PackageReview from './PackageReview'
import { PrivacyOptions, StatusNotice, WorkflowFooter, WorkflowHeading } from './WorkflowLayout'

const destinationsSchema = z.object({
  destinations: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      origin: z.string(),
      descriptor: InstallationDescriptor,
      canSend: z.boolean(),
    })
  ),
})
const path = (value: string) => `show-deliveries/${value}`
export default function DirectDelivery({
  showId,
  installationKey,
  disabled,
  onLocked,
  view = 'all',
}: {
  showId: string
  view?: 'all' | 'send-copy' | 'send-move' | 'inbox' | 'recover'
  installationKey: string
  disabled: boolean
  onLocked: (locked: boolean) => void
}) {
  const [destinations, setDestinations] = useState<
    z.infer<typeof destinationsSchema>['destinations']
  >([])
  const [pageState, setPageState] = useState<{
    showId: string
    rows: TDeliveryStatus[]
    nextCursor: string | null
  }>({ showId, rows: [], nextCursor: null })
  const rows = pageState.showId === showId ? pageState.rows : []
  const nextCursor = pageState.showId === showId ? pageState.nextCursor : null
  const loadGeneration = useRef(0)
  const [remote, setRemote] = useState<Record<string, TDeliveryStatus>>({})
  const [peerId, setPeerId] = useState('')
  const [profile, setProfile] = useState<'limited' | 'full-contact'>('limited')
  const [contacts, setContacts] = useState(false)
  const [synced, setSynced] = useState<string | null>(null)
  const [release, setRelease] = useState(false)
  const [downloadAck, setDownloadAck] = useState(false)
  const [completedEnvelope, setCompletedEnvelope] = useState<string>()
  const [envelope, setEnvelope] = useState<TDeliveryEnvelope>()
  const [message, setMessage] = useState('')
  const [recoveredReceipt, setRecoveredReceipt] = useState<unknown>()
  const [uncertain, setUncertain] = useState(false)
  const [listStale, setListStale] = useState(false)
  const [reviewLocked, setReviewLocked] = useState(false)
  const [reading, setReading] = useState(false)
  const running = useRef(false)
  const retry = useRef<(() => Promise<void>) | null>(null)
  const action = useTransferAction(async (operation: () => Promise<void>) => operation())
  const busy = disabled || action.isPending || reviewLocked || reading
  const locked = busy || uncertain
  useEffect(() => {
    onLocked(action.isPending || reviewLocked || reading || uncertain)
  }, [action.isPending, reviewLocked, reading, uncertain, onLocked])
  async function load(cursor?: string) {
    const generation = ++loadGeneration.current
    const query = new URLSearchParams()
    if (showId) query.set('showId', showId)
    if (cursor) query.set('cursor', cursor)
    const [peers, list] = await Promise.all([
      cursor ? undefined : transferRequest(path('destinations')),
      transferRequest(path(query.size ? `?${query}` : '')),
    ])
    if (generation !== loadGeneration.current) return
    if (peers) setDestinations(destinationsSchema.parse(peers).destinations)
    const page = DeliveryPage.parse(list)
    if (!cursor) setListStale(false)
    setPageState(current => ({
      showId,
      rows:
        cursor && current.showId === showId
          ? [...current.rows, ...page.deliveries]
          : page.deliveries,
      nextCursor: page.nextCursor,
    }))
  }
  useEffect(() => {
    setSynced(null)
    setContacts(false)
    setEnvelope(undefined)
    setCompletedEnvelope(undefined)
    setRecoveredReceipt(undefined)
    setListStale(false)
    setPageState({ showId, rows: [], nextCursor: null })
    const pending = load()
    const generation = loadGeneration.current
    void pending.catch(() => {
      if (generation === loadGeneration.current)
        setMessage('Unable to load direct deliveries. Refresh after reconnecting.')
    })
    return () => {
      loadGeneration.current++
    }
  }, [showId])
  // After a completed operation only the list is stale: report that separately, keep the
  // operation's own message, and never offer to repeat the operation.
  async function refreshAfterChange() {
    const pending = load()
    const generation = loadGeneration.current
    try {
      await pending
    } catch {
      // A newer load (or another show) supersedes this one, including its failure.
      if (generation === loadGeneration.current) setListStale(true)
    }
  }
  async function run(
    operation: () => Promise<void>,
    { resolves = true, refresh = true }: { resolves?: boolean; refresh?: boolean } = {}
  ) {
    if (running.current || busy) return
    running.current = true
    if (resolves) retry.current = operation
    setMessage('')
    try {
      try {
        await action.mutateAsync(operation)
      } catch (error) {
        if (!resolves) {
          // A failed read changed nothing: keep the workspace usable and the retry target intact.
          setMessage('Unable to load direct deliveries. Refresh after reconnecting.')
          return
        }
        setUncertain(isUncertainTransferFailure(error))
        setMessage(
          'Delivery could not be completed. Retry the unchanged operation or refresh its status. A missing response never permits source reactivation.'
        )
        return
      }
      if (resolves) setUncertain(false)
      if (refresh) await refreshAfterChange()
    } finally {
      running.current = false
    }
  }
  function start(kind: 'copy' | 'request') {
    const body = { deliveryKey: crypto.randomUUID(), peerId, showId, kind, privacyProfile: profile }
    void run(async () => {
      await transferRequest(path('create'), body)
      const status = DeliveryStatus.parse(
        await transferRequest(path(`outgoing/${body.deliveryKey}/send`), {})
      )
      setRemote(current => ({ ...current, [body.deliveryKey]: status }))
      setMessage(
        'Delivered for destination administrator review. Delivery alone does not import or activate a show.'
      )
    })
  }
  const destination = destinations.find(d => d.id === peerId)
  return (
    <section aria-label="Direct delivery" className="space-y-6">
      <WorkflowHeading>
        {view === 'inbox'
          ? 'Received deliveries'
          : view === 'recover'
            ? 'Recover a delivery'
            : 'Send to a connected server'}
      </WorkflowHeading>
      <p>
        Encrypted HTTPS delivery. The destination administrator must review and apply received data.
      </p>
      {!destinations.length && view !== 'inbox' && view !== 'recover' && (
        <StatusNotice title="No connected servers">
          No paired destinations are configured. Ask the server operator to configure pairing and
          certificate trust.
        </StatusNotice>
      )}
      <fieldset
        hidden={view === 'inbox' || view === 'recover'}
        disabled={locked}
        className="space-y-3"
      >
        <label>
          Destination
          <select
            className="block w-full max-w-full border rounded p-2"
            value={peerId}
            onChange={e => {
              setPeerId(e.target.value)
              setContacts(false)
            }}
          >
            <option value="">Select a destination</option>
            {destinations
              .filter(d => d.canSend)
              .map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} — {d.origin}
                </option>
              ))}
          </select>
        </label>
        {destination && (
          <p className="break-all">
            Verified pairing: {destination.descriptor.installationKey} ·{' '}
            {destination.descriptor.fingerprint}
          </p>
        )}
        <PrivacyOptions
          value={profile}
          legend="Personal information to send"
          onChange={value => {
            setProfile(value)
            setContacts(false)
          }}
        />
        {profile === 'full-contact' && (
          <StatusNotice tone="warning" title="Contact details will be shared">
            <Check checked={contacts} onChange={setContacts}>
              I authorize sending contact details to this destination.
            </Check>
          </StatusNotice>
        )}
        <WorkflowFooter>
          <Button
            hidden={view === 'send-move'}
            disabled={!showId || !destination?.canSend || (profile === 'full-contact' && !contacts)}
            onClick={() => start('copy')}
          >
            Send copy
          </Button>{' '}
          <Button
            hidden={view === 'send-copy'}
            disabled={!showId || !destination?.canSend || (profile === 'full-contact' && !contacts)}
            onClick={() => start('request')}
          >
            Hand over editing
          </Button>
        </WorkflowFooter>
        {view !== 'send-copy' && (
          <p>
            Hand over editing first asks the destination administrator to prepare. The source stays
            active until you confirm synchronization and freeze it.
          </p>
        )}
      </fieldset>
      {message && (
        <StatusNotice
          tone={
            uncertain
              ? 'warning'
              : action.isError
                ? 'error'
                : message.startsWith('Unable')
                  ? 'error'
                  : 'info'
          }
          role={uncertain ? 'alert' : 'status'}
        >
          {message}
        </StatusNotice>
      )}
      {listStale && (
        <StatusNotice tone="warning" role="status">
          The delivery list could not be refreshed and may be out of date. Open Delivery history and
          recovery and choose Refresh delivery list to try again.
        </StatusNotice>
      )}
      {uncertain && (
        <Button disabled={busy} onClick={() => retry.current && void run(retry.current)}>
          Retry unchanged delivery operation
        </Button>
      )}
      <details open={view !== 'send-copy'}>
        <summary className="text-sm font-medium">Delivery history and recovery</summary>
        <div className="mt-4 space-y-4">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => void run(load, { resolves: false, refresh: false })}
          >
            Refresh delivery list
          </Button>
          <Check checked={downloadAck} onChange={setDownloadAck} disabled={locked}>
            I understand recovery downloads contain sensitive plaintext JSON.
          </Check>
          <h3 className="font-semibold">Recent deliveries</h3>
          {!rows.length && (
            <p className="text-sm text-muted-foreground">
              No deliveries to show. Refresh to check for new arrivals.
            </p>
          )}
          <ul className="space-y-3">
            {rows.map(row => (
              <li key={`${row.direction}-${row.deliveryKey}`} className="rounded-lg border p-4">
                <details open={view === 'all'} className="space-y-3">
                  <summary className="cursor-pointer font-medium">
                    {row.direction === 'incoming' ? 'Received' : 'Sent'} ·{' '}
                    {row.kind === 'copy'
                      ? 'Show copy'
                      : row.kind === 'request'
                        ? 'Transfer request'
                        : 'Transfer bundle'}{' '}
                    · {row.peerId} · {remote[row.deliveryKey]?.state ?? row.state}
                  </summary>
                  <p className="break-all text-sm">
                    {row.deliveryKey} · Staging expires {row.expiresAt}
                  </p>
                  {Boolean(row.receipt) && (
                    <div>
                      <p role="status">
                        {row.state === 'cancelled'
                          ? 'Signed cancellation recorded.'
                          : 'Committed receipt recorded. No further import is needed.'}
                      </p>
                      <Button
                        disabled={locked || !downloadAck}
                        onClick={() =>
                          downloadJson(row.receipt, `delivery-${row.deliveryKey}-receipt.json`)
                        }
                      >
                        Download receipt
                      </Button>
                    </div>
                  )}
                  {row.direction === 'outgoing' && (
                    <>
                      <Button
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            const status = DeliveryStatus.parse(
                              await transferRequest(path(`outgoing/${row.deliveryKey}/refresh`), {})
                            )
                            setRemote(current => ({ ...current, [row.deliveryKey]: status }))
                            setMessage(
                              status.receipt
                                ? 'Established receipt recovered and recorded.'
                                : 'Destination status refreshed; this is not a committed import receipt.'
                            )
                          })
                        }
                      >
                        Check destination status / receipt
                      </Button>{' '}
                      {!row.receipt && (
                        <Button
                          disabled={locked}
                          onClick={() =>
                            void run(async () => {
                              const status = DeliveryStatus.parse(
                                await transferRequest(path(`outgoing/${row.deliveryKey}/send`), {})
                              )
                              setRemote(current => ({ ...current, [row.deliveryKey]: status }))
                            })
                          }
                        >
                          Retry exact delivery
                        </Button>
                      )}
                      {row.kind === 'request' && !row.receipt && (
                        <>
                          <Check
                            checked={synced === row.deliveryKey}
                            onChange={checked => setSynced(checked ? row.deliveryKey : null)}
                            disabled={locked}
                          >
                            All intake devices for the selected show are synchronized. I authorize
                            freezing this handover ({row.deliveryKey}).
                          </Check>
                          <Button
                            variant="destructive"
                            disabled={locked || synced !== row.deliveryKey}
                            onClick={() => {
                              const bundleKey = crypto.randomUUID()
                              void run(async () => {
                                await transferRequest(path(`outgoing/${row.deliveryKey}/freeze`), {
                                  bundleKey,
                                  showId,
                                  intakeDevicesSynced: true,
                                })
                                await transferRequest(path(`outgoing/${bundleKey}/send`), {})
                                setSynced(null)
                                setMessage(
                                  'Source frozen; bundle delivered for destination review. Recover the signed receipt before changing source ownership.'
                                )
                              })
                            }}
                          >
                            Continue prepared handover and freeze
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  {!row.receipt && row.state !== 'expired' && row.state !== 'discarded' && (
                    <>
                      <Button
                        variant="outline"
                        disabled={locked || !downloadAck}
                        onClick={() =>
                          void run(async () => {
                            downloadJson(
                              await transferRequest(
                                path(`${row.direction}/${row.deliveryKey}/payload`)
                              ),
                              `delivery-${row.deliveryKey}.json`,
                              true
                            )
                          })
                        }
                      >
                        Download exact recovery envelope
                      </Button>
                      {row.direction === 'incoming' && (
                        <Button
                          disabled={locked}
                          onClick={() =>
                            void run(async () => {
                              setEnvelope(
                                DeliveryEnvelope.parse(
                                  await transferRequest(path(`incoming/${row.deliveryKey}/payload`))
                                )
                              )
                              setRelease(false)
                            })
                          }
                        >
                          Review received delivery
                        </Button>
                      )}
                    </>
                  )}
                </details>
              </li>
            ))}
          </ul>
          {nextCursor && (
            <Button
              disabled={busy}
              onClick={() => void run(() => load(nextCursor), { resolves: false, refresh: false })}
            >
              Load older deliveries
            </Button>
          )}
          <StatusNotice tone="warning" title="Keep recovery files secure">
            {privacyWarning} A recovery file retains the original delivery identity.
          </StatusNotice>
          <JsonFile
            label="Load delivery recovery envelope"
            disabled={locked}
            onReading={setReading}
            onLoad={value => {
              const saved = DeliveryEnvelope.parse(value)
              void run(async () => {
                const result = DeliveryStatus.parse(await transferRequest(path('adopt'), saved))
                setRecoveredReceipt(result.receipt ?? undefined)
                setEnvelope(result.receipt ? undefined : saved)
                setRelease(false)
                setMessage(
                  result.receipt
                    ? 'Established receipt recovered; no import or activation was repeated.'
                    : 'File delivery loaded for administrator review.'
                )
              })
            }}
          />
          {Boolean(recoveredReceipt) && (
            <Button
              disabled={locked || !downloadAck}
              onClick={() => downloadJson(recoveredReceipt, 'delivery-receipt.json')}
            >
              Download established receipt
            </Button>
          )}
        </div>
      </details>
      {envelope?.content.kind === 'request' && (
        <div className="space-y-3">
          <p>
            Requested privacy: {envelope.content.preparation.privacyProfile}. Preparing releases a
            snapshot of this destination’s matching show to the paired source, including contacts
            only for full-contact.
          </p>
          <Check checked={release} onChange={setRelease} disabled={locked}>
            I approve this preparation and checkpoint release to the verified source.
          </Check>
          <Button
            disabled={locked || !release}
            onClick={() =>
              void run(async () => {
                await transferRequest(path(`incoming/${envelope.deliveryKey}/prepare`), {
                  confirmCheckpointRelease: true,
                })
                setEnvelope(undefined)
                setMessage(
                  'Preparation released. The source administrator can now confirm device synchronization and freeze the source.'
                )
              })
            }
          >
            Approve preparation
          </Button>
        </div>
      )}
      {envelope && envelope.content.kind !== 'request' && (
        <PackageReview
          key={envelope.deliveryKey}
          delivery={envelope}
          packageValue={
            envelope.content.kind === 'copy'
              ? envelope.content.package
              : envelope.content.bundle.package
          }
          bundle={envelope.content.kind === 'bundle' ? envelope.content.bundle : undefined}
          installationKey={installationKey}
          disabled={disabled || action.isPending || reading}
          onLocked={setReviewLocked}
          onFinished={() => {
            setCompletedEnvelope(envelope.deliveryKey)
            void refreshAfterChange()
          }}
        />
      )}
      {envelope && completedEnvelope !== envelope.deliveryKey && (
        <Button
          variant="destructive"
          disabled={locked}
          onClick={() =>
            void run(async () => {
              const result = DeliveryStatus.parse(
                await transferRequest(path(`incoming/${envelope.deliveryKey}/cancel`), { envelope })
              )
              setRecoveredReceipt(result.receipt ?? undefined)
              setEnvelope(undefined)
              setMessage(
                result.state === 'committed'
                  ? 'This delivery was already committed; no cancellation occurred. Recover the established receipt.'
                  : result.state === 'cancelled'
                    ? 'Signed cancellation recorded. Return the receipt to the source to restore editing.'
                    : 'Staged delivery discarded. Show ownership has not changed.'
              )
            })
          }
        >
          {envelope.content.kind === 'bundle'
            ? 'Cancel handover at destination'
            : 'Discard staged delivery'}
        </Button>
      )}
    </section>
  )
}
